import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for fetching and managing reminders
export const fetchReminders = createAsyncThunk(
  'reminders/fetchReminders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('reminders');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const fetchReminderById = createAsyncThunk(
  'reminders/fetchReminderById',
  async (reminderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`reminders/${reminderId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const createReminder = createAsyncThunk(
  'reminders/createReminder',
  async (reminderData, { rejectWithValue }) => {
    try {
      const response = await api.post('reminders', reminderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const updateReminderStatus = createAsyncThunk(
  'reminders/updateReminderStatus',
  async ({ reminderId, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`reminders/${reminderId}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const cancelReminder = createAsyncThunk(
  'reminders/cancelReminder',
  async ({ reminderId, pausePledge }, { rejectWithValue }) => {
    try {
      await api.delete(`reminders/${reminderId}`, {
        data: { pausePledge },
      });
      return { reminderId, pausePledge };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

const reminderSlice = createSlice({
  name: 'reminders',
  initialState: {
    reminders: [],
    currentReminder: null,
    loading: false,
    error: null,
    statusFilter: null, // null, 'PENDING', 'COMPLETED', 'OVERDUE'
  },
  reducers: {
    // Clear error messages
    clearError: (state) => {
      state.error = null;
    },

    // Clear current reminder
    clearCurrentReminder: (state) => {
      state.currentReminder = null;
    },

    // Set status filter
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },

    // Update reminder status locally (for optimistic updates)
    updateReminderStatusLocal: (state, action) => {
      const { id, status } = action.payload;
      const reminder = state.reminders.find((r) => r.id === id);
      if (reminder) {
        reminder.status = status;
      }
      if (state.currentReminder?.id === id) {
        state.currentReminder.status = status;
      }
    },

    // Remove reminder from local state (for optimistic updates)
    removeReminderLocal: (state, action) => {
      state.reminders = state.reminders.filter((r) => r.id !== action.payload);
      if (state.currentReminder?.id === action.payload) {
        state.currentReminder = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch all reminders
    builder
      .addCase(fetchReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.reminders = action.payload;
        state.error = null;
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch reminder by ID
    builder
      .addCase(fetchReminderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReminderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReminder = action.payload;
        state.error = null;
      })
      .addCase(fetchReminderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create reminder
    builder
      .addCase(createReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.reminders.push(action.payload);
        state.error = null;
      })
      .addCase(createReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update reminder status
    builder
      .addCase(updateReminderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReminderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.payload;
        const reminder = state.reminders.find((r) => r.id === id);
        if (reminder) {
          reminder.status = status;
        }
        if (state.currentReminder?.id === id) {
          state.currentReminder.status = status;
        }
        state.error = null;
      })
      .addCase(updateReminderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Cancel reminder
    builder
      .addCase(cancelReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelReminder.fulfilled, (state, action) => {
        state.loading = false;
        const { reminderId } = action.payload;
        state.reminders = state.reminders.filter((r) => r.id !== reminderId);
        if (state.currentReminder?.id === reminderId) {
          state.currentReminder = null;
        }
        state.error = null;
      })
      .addCase(cancelReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearCurrentReminder,
  setStatusFilter,
  updateReminderStatusLocal,
  removeReminderLocal,
} = reminderSlice.actions;

// Selectors
export const selectAllReminders = (state) => state.reminders.reminders;
export const selectCurrentReminder = (state) => state.reminders.currentReminder;
export const selectRemindersLoading = (state) => state.reminders.loading;
export const selectRemindersError = (state) => state.reminders.error;
export const selectStatusFilter = (state) => state.reminders.statusFilter;

// Selector to get filtered reminders based on status filter
export const selectFilteredReminders = (state) => {
  const { reminders, statusFilter } = state.reminders;
  if (!statusFilter) {
    return reminders;
  }
  return reminders.filter((r) => r.status === statusFilter);
};

// Selector to get reminder by ID
export const selectReminderById = (state, reminderId) => {
  return state.reminders.reminders.find((r) => r.id === reminderId);
};

// Selector to filter reminders by status
export const selectRemindersByStatus = (state, status) => {
  return state.reminders.reminders.filter((r) => r.status === status);
};

// Selector to filter reminders by pledge
export const selectRemindersByPledge = (state, pledgeId) => {
  return state.reminders.reminders.filter((r) => r.donation_id === pledgeId);
};

// Selector to filter reminders by contact
export const selectRemindersByContact = (state, contactId) => {
  return state.reminders.reminders.filter((r) => r.contact_id === contactId);
};

// Selector to get pending reminders count
export const selectPendingRemindersCount = (state) => {
  return state.reminders.reminders.filter((r) => r.status === 'PENDING').length;
};

// Selector to get overdue reminders count
export const selectOverdueRemindersCount = (state) => {
  return state.reminders.reminders.filter((r) => r.status === 'OVERDUE').length;
};

export default reminderSlice.reducer;
